import { useState, useEffect, useCallback } from "react";
import { gemsAPI } from "../services/api";
import {
  Gem, Search, RefreshCw, TrendingUp, TrendingDown,
  ChevronDown, ChevronUp, Sparkles, Target, ShieldAlert,
  Building2, Zap, Brain, Rocket, Filter, ArrowUpRight,
  DollarSign, BarChart3, Users, Eye, Globe, AlertTriangle, UserCheck,
  Trophy, Crown, Medal, Star, Bookmark, ArrowUp, ArrowDown
} from "lucide-react";

const GRADE_COLORS = {
  S: "from-yellow-400 to-amber-500 text-black",
  A: "from-emerald-400 to-green-500 text-black",
  B: "from-cyan-400 to-blue-500 text-white",
  C: "from-slate-400 to-slate-500 text-white",
  D: "from-red-400 to-red-500 text-white",
};

const GRADE_BG = {
  S: "bg-yellow-500/10 border-yellow-500/30",
  A: "bg-emerald-500/10 border-emerald-500/30",
  B: "bg-cyan-500/10 border-cyan-500/30",
  C: "bg-slate-500/10 border-slate-500/30",
  D: "bg-red-500/10 border-red-500/30",
};

const SECTOR_ICONS = {
  "AI Infrastructure": Brain,
  "Quantum Computing": Sparkles,
  "Cybersecurity": ShieldAlert,
  "Robotics Automation": Zap,
  "Space Defense": Rocket,
  "Clean Energy": Zap,
  "Biotech Genomics": Target,
  "Fintech": DollarSign,
  "EV Mobility": TrendingUp,
  "SaaS Cloud": Building2,
  "China Tech": Building2,
  "Social Media Commerce": Users,
  "Semiconductor Next": Zap,
  "Healthcare Tech": Target,
  "EU Tech Software": Globe,
  "EU Semiconductor": Zap,
  "EU Defense": ShieldAlert,
  "EU Biotech": Target,
  "EU Clean Energy": Zap,
  "EU Fintech": DollarSign,
  "EU Luxury Consumer": Gem,
  "EU Industry Growth": Building2,
};

function formatMCap(val) {
  if (!val) return "—";
  if (val >= 1e12) return `$${(val / 1e12).toFixed(1)}T`;
  if (val >= 1e9) return `$${(val / 1e9).toFixed(1)}B`;
  if (val >= 1e6) return `$${(val / 1e6).toFixed(0)}M`;
  return `$${val.toLocaleString()}`;
}

function ScoreBar({ label, value, max, color }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-24 text-slate-400 truncate">{label}</span>
      <div className="flex-1 h-1.5 bg-slate-700 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full ${color}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="w-8 text-right text-slate-300 font-mono">{value}/{max}</span>
    </div>
  );
}

function GemCard({ gem, rank, onSelect, onThesis, thesisLoading, isInWatchlist, onToggleWatchlist }) {
  const [expanded, setExpanded] = useState(false);
  const scores = gem.scores || {};
  const details = gem.details || {};
  const gradeClass = GRADE_COLORS[gem.grade] || GRADE_COLORS.C;
  const bgClass = GRADE_BG[gem.grade] || GRADE_BG.C;

  return (
    <div
      className={`border rounded-xl p-4 transition-all hover:shadow-lg hover:shadow-cyan-500/5 cursor-pointer relative ${bgClass}`}
      data-testid={`gem-card-${gem.ticker}`}
      onClick={() => onSelect(gem.ticker)}
    >
      {/* Watchlist toggle */}
      <button
        onClick={(e) => { e.stopPropagation(); onToggleWatchlist?.(gem.ticker); }}
        className={`absolute top-2 right-2 p-1.5 rounded-lg transition-all z-10 ${isInWatchlist ? "bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 hover:bg-yellow-500/30" : "bg-slate-800/60 text-slate-600 border border-slate-700/30 hover:text-yellow-400 hover:border-yellow-500/30"}`}
        data-testid={`gem-watchlist-toggle-${gem.ticker}`}
        title={isInWatchlist ? "Retirer de la watchlist" : "Ajouter a la watchlist"}
      >
        <Star className={`w-3.5 h-3.5 ${isInWatchlist ? "fill-current" : ""}`} />
      </button>
      {/* Header */}
      <div className="flex items-start justify-between mb-3 pr-8">
        <div className="flex items-center gap-3">
          <span className="text-lg font-bold text-slate-300 opacity-40 w-6">
            {rank}
          </span>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-white">{gem.ticker}</span>
              <span
                className={`px-2 py-0.5 rounded-md text-xs font-black bg-gradient-to-r ${gradeClass}`}
              >
                {gem.grade}
              </span>
              <span className="text-sm font-bold text-cyan-400">
                {gem.total_score}/100
              </span>
              {[".PA", ".DE", ".AS", ".L", ".MI", ".ST", ".CO", ".SW", ".OL", ".HE"].some(s => gem.ticker.endsWith(s)) && (
                <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-400 border border-blue-500/20 font-bold">EU</span>
              )}
            </div>
            <p className="text-xs text-slate-400 truncate max-w-[280px]">
              {gem.name}
            </p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-lg font-bold text-white">
            ${gem.price?.toFixed(2)}
          </div>
          <div className="text-xs text-slate-400">
            {formatMCap(gem.market_cap)}
          </div>
          {/* Performance badges */}
          <div className="flex items-center gap-1 mt-1 justify-end flex-wrap">
            {gem.perf_1m != null && (
              <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${gem.perf_1m >= 0 ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`}>
                1M {gem.perf_1m >= 0 ? "+" : ""}{gem.perf_1m}%
              </span>
            )}
            {gem.perf_6m != null && (
              <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${gem.perf_6m >= 0 ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`}>
                6M {gem.perf_6m >= 0 ? "+" : ""}{gem.perf_6m}%
              </span>
            )}
          </div>
          {gem.perf_since_detect != null && (
            <div className={`mt-1 text-[9px] font-bold ${gem.perf_since_detect >= 0 ? "text-emerald-400" : "text-red-400"}`}>
              Depuis detection: {gem.perf_since_detect >= 0 ? "+" : ""}{gem.perf_since_detect}%
            </div>
          )}
        </div>
      </div>

      {/* Themes */}
      <div className="flex flex-wrap gap-1 mb-3">
        {(gem.themes || []).map((theme) => {
          const Icon = SECTOR_ICONS[theme] || Gem;
          return (
            <span
              key={theme}
              className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full bg-slate-700/50 text-slate-300"
            >
              <Icon className="w-3 h-3" />
              {theme}
            </span>
          );
        })}
      </div>

      {/* Key Metrics Row */}
      <div className="grid grid-cols-4 gap-2 mb-3">
        <div className="text-center p-1.5 rounded-lg bg-slate-800/50">
          <div className="text-[10px] text-slate-500 uppercase">Revenue</div>
          <div
            className={`text-sm font-bold ${
              (details.revenue_growth_pct || 0) > 20
                ? "text-emerald-400"
                : (details.revenue_growth_pct || 0) > 0
                ? "text-cyan-400"
                : "text-red-400"
            }`}
          >
            {details.revenue_growth_pct != null
              ? `${details.revenue_growth_pct > 0 ? "+" : ""}${details.revenue_growth_pct}%`
              : "—"}
          </div>
        </div>
        <div className="text-center p-1.5 rounded-lg bg-slate-800/50">
          <div className="text-[10px] text-slate-500 uppercase">Insiders</div>
          <div
            className={`text-sm font-bold ${
              (details.insider_net || 0) > 0
                ? "text-emerald-400"
                : (details.insider_net || 0) < 0
                ? "text-red-400"
                : "text-slate-400"
            }`}
          >
            {details.insider_net != null
              ? `${details.insider_net > 0 ? "+" : ""}${details.insider_net}`
              : "—"}
          </div>
        </div>
        <div className="text-center p-1.5 rounded-lg bg-slate-800/50">
          <div className="text-[10px] text-slate-500 uppercase">Marge</div>
          <div className="text-sm font-bold text-slate-300">
            {details.gross_margin != null
              ? `${details.gross_margin}%`
              : "—"}
          </div>
        </div>
        <div className="text-center p-1.5 rounded-lg bg-slate-800/50">
          <div className="text-[10px] text-slate-500 uppercase">
            {details.peg_ratio ? "PEG" : "P/E"}
          </div>
          <div className="text-sm font-bold text-slate-300">
            {details.peg_ratio != null
              ? details.peg_ratio
              : details.forward_pe != null
              ? details.forward_pe
              : "—"}
          </div>
        </div>
      </div>

      {/* Score Breakdown (expandable) */}
      <button
        onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
        className="w-full flex items-center justify-between text-xs text-slate-400 hover:text-slate-200 transition-colors py-1"
        data-testid={`gem-expand-${gem.ticker}`}
      >
        <span>Score Breakdown</span>
        {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>

      {expanded && (
        <div className="mt-2 space-y-1.5">
          <ScoreBar label="Revenue Growth" value={scores.revenue_growth || 0} max={25} color="bg-emerald-500" />
          <ScoreBar label="Insider Buying" value={scores.insider_buying || 0} max={20} color="bg-yellow-500" />
          <ScoreBar label="Institutional" value={scores.institutional || 0} max={15} color="bg-blue-500" />
          <ScoreBar label="Valuation" value={scores.valuation || 0} max={15} color="bg-purple-500" />
          <ScoreBar label="Profitability" value={scores.profitability || 0} max={10} color="bg-cyan-500" />
          <ScoreBar label="Momentum" value={scores.momentum || 0} max={10} color="bg-orange-500" />
          <ScoreBar label="Catalyst" value={scores.catalyst_bonus || 0} max={5} color="bg-pink-500" />

          {/* Thesis */}
          {gem.thesis ? (
            <div className="mt-3 p-3 rounded-lg bg-slate-800/80 text-xs text-slate-300 whitespace-pre-line leading-relaxed">
              {gem.thesis}
            </div>
          ) : (
            <button
              onClick={(e) => { e.stopPropagation(); onThesis(gem.ticker); }}
              disabled={thesisLoading}
              className="mt-2 w-full py-2 text-xs font-medium rounded-lg bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 text-cyan-400 hover:text-white hover:border-cyan-400 transition-all disabled:opacity-50"
              data-testid={`gem-thesis-${gem.ticker}`}
            >
              {thesisLoading ? "Analyse IA en cours..." : "Generer These IA"}
            </button>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 mt-2">
            <button
              onClick={(e) => { e.stopPropagation(); onSelect(gem.ticker); }}
              className="flex-1 py-1.5 text-xs font-medium rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-300 transition-all flex items-center justify-center gap-1"
              data-testid={`gem-analyze-${gem.ticker}`}
            >
              <BarChart3 className="w-3 h-3" /> Analyse Algo
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function GemsTab({ onAnalyze, owner }) {
  const [gems, setGems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sectorFilter, setSectorFilter] = useState("");
  const [gradeFilter, setGradeFilter] = useState("");
  const [regionFilter, setRegionFilter] = useState("");
  const [thesisLoading, setThesisLoading] = useState("");
  const [totalScanned, setTotalScanned] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [insiderAlerts, setInsiderAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [hallOfFame, setHallOfFame] = useState(null);
  const [showHallOfFame, setShowHallOfFame] = useState(false);
  const [watchlist, setWatchlist] = useState([]);
  const [watchlistTickers, setWatchlistTickers] = useState(new Set());
  const [showWatchlist, setShowWatchlist] = useState(true);

  const loadGems = useCallback(async () => {
    setLoading(true);
    try {
      const data = await gemsAPI.getTopGems(80);
      setGems(data.gems || []);
      setTotalScanned(data.total_scanned || 0);
      setUpdatedAt(data.updated_at);
    } catch (err) {
      console.error("Failed to load gems:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadInsiderAlerts = useCallback(async () => {
    setAlertsLoading(true);
    try {
      const data = await gemsAPI.getInsiderAlerts();
      setInsiderAlerts(data.alerts || []);
    } catch (err) {
      console.error("Failed to load insider alerts:", err);
    } finally {
      setAlertsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGems();
    loadInsiderAlerts();
    // Load hall of fame
    gemsAPI.getHallOfFame().then(data => setHallOfFame(data)).catch(() => {});
  }, [loadGems, loadInsiderAlerts]);

  // Load watchlist
  const loadWatchlist = useCallback(async () => {
    if (!owner) return;
    try {
      const data = await gemsAPI.getWatchlist(owner);
      setWatchlist(data.watchlist || []);
      setWatchlistTickers(new Set((data.watchlist || []).map(w => w.ticker)));
    } catch (err) {
      console.error("Failed to load watchlist:", err);
    }
  }, [owner]);

  useEffect(() => {
    loadWatchlist();
  }, [loadWatchlist]);

  const toggleWatchlist = async (ticker) => {
    if (!owner) return;
    const isIn = watchlistTickers.has(ticker);
    try {
      if (isIn) {
        await gemsAPI.removeFromWatchlist(owner, ticker);
        setWatchlistTickers(prev => { const n = new Set(prev); n.delete(ticker); return n; });
        setWatchlist(prev => prev.filter(w => w.ticker !== ticker));
      } else {
        const res = await gemsAPI.addToWatchlist(ticker, owner);
        if (res.ok) {
          setWatchlistTickers(prev => new Set(prev).add(ticker));
          loadWatchlist();
        }
      }
    } catch (err) {
      console.error("Watchlist toggle error:", err);
    }
  };

  const handleScan = async () => {
    setScanning(true);
    try {
      await gemsAPI.triggerScan();
      await loadGems();
    } catch (err) {
      console.error("Scan failed:", err);
    } finally {
      setScanning(false);
    }
  };

  const handleThesis = async (ticker) => {
    setThesisLoading(ticker);
    try {
      const data = await gemsAPI.getThesis(ticker);
      setGems((prev) =>
        prev.map((g) =>
          g.ticker === ticker ? { ...g, thesis: data.thesis } : g
        )
      );
    } catch (err) {
      console.error("Thesis failed:", err);
    } finally {
      setThesisLoading("");
    }
  };

  // Filter gems
  const EU_SUFFIXES = [".PA", ".DE", ".AS", ".L", ".MI", ".ST", ".CO", ".SW", ".OL", ".HE"];
  const isEU = (ticker) => EU_SUFFIXES.some(s => ticker.endsWith(s));

  const filtered = gems.filter((g) => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (
        !g.ticker.toLowerCase().includes(q) &&
        !(g.name || "").toLowerCase().includes(q) &&
        !(g.themes || []).some((t) => t.toLowerCase().includes(q))
      )
        return false;
    }
    if (sectorFilter) {
      if (!(g.themes || []).some((t) => t.includes(sectorFilter.replace("_", " "))))
        return false;
    }
    if (gradeFilter && g.grade !== gradeFilter) return false;
    if (regionFilter === "EU" && !isEU(g.ticker)) return false;
    if (regionFilter === "US" && isEU(g.ticker)) return false;
    return true;
  });

  // Stats
  const euCount = gems.filter((g) => isEU(g.ticker)).length;
  const usCount = gems.length - euCount;

  // Stats
  const sGrade = gems.filter((g) => g.grade === "S").length;
  const aGrade = gems.filter((g) => g.grade === "A").length;
  const avgScore = gems.length > 0
    ? Math.round(gems.reduce((s, g) => s + g.total_score, 0) / gems.length)
    : 0;

  const sectors = [...new Set(gems.flatMap((g) => g.themes || []))].sort();

  return (
    <div className="space-y-4" data-testid="gems-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Gem className="w-5 h-5 text-yellow-400" />
            Pepites — Future 10x Stocks
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">
            {totalScanned} actions analysees | Score 0-100 | Revenue + Insiders + Institutions + Valorisation + Momentum
            {updatedAt && (
              <span className="ml-2 text-slate-500">
                Mis a jour: {new Date(updatedAt).toLocaleString("fr-FR")}
              </span>
            )}
          </p>
        </div>
        <button
          onClick={handleScan}
          disabled={scanning}
          className="px-4 py-2 text-xs font-bold rounded-lg bg-gradient-to-r from-yellow-500/20 to-amber-500/20 border border-yellow-500/30 text-yellow-400 hover:text-white hover:border-yellow-400 transition-all disabled:opacity-50 flex items-center gap-2"
          data-testid="gems-scan-btn"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${scanning ? "animate-spin" : ""}`} />
          {scanning ? "Scan en cours..." : "Nouveau Scan"}
        </button>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <div className="p-3 rounded-xl bg-slate-800/50 border border-slate-700/50">
          <div className="text-[10px] text-slate-500 uppercase">S-Tier + Grade A</div>
          <div className="text-2xl font-black text-yellow-400">{sGrade + aGrade}</div>
        </div>
        <div className="p-3 rounded-xl bg-slate-800/50 border border-slate-700/50">
          <div className="text-[10px] text-slate-500 uppercase">US</div>
          <div className="text-2xl font-black text-cyan-400">{usCount}</div>
        </div>
        <div className="p-3 rounded-xl bg-slate-800/50 border border-slate-700/50">
          <div className="text-[10px] text-slate-500 uppercase">Europe</div>
          <div className="text-2xl font-black text-blue-400">{euCount}</div>
        </div>
        <div className="p-3 rounded-xl bg-slate-800/50 border border-slate-700/50">
          <div className="text-[10px] text-slate-500 uppercase">Score Moyen</div>
          <div className="text-2xl font-black text-slate-300">{avgScore}</div>
        </div>
        <div className="p-3 rounded-xl bg-slate-800/50 border border-slate-700/50">
          <div className="text-[10px] text-slate-500 uppercase">Insider Alerts</div>
          <div className="text-2xl font-black text-amber-400">{insiderAlerts.length}</div>
        </div>
      </div>

      {/* MA WATCHLIST */}
      {watchlist.length > 0 && (
        <div className="p-4 rounded-xl bg-gradient-to-r from-yellow-500/5 to-amber-500/5 border border-yellow-500/20" data-testid="gems-watchlist-section">
          <button onClick={() => setShowWatchlist(!showWatchlist)} className="w-full flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-400 fill-current" />
              <span className="text-sm font-black text-white">Ma Watchlist</span>
              <span className="px-2 py-0.5 rounded text-[9px] font-bold bg-yellow-500/15 text-yellow-400 border border-yellow-500/20">
                {watchlist.length} pepite{watchlist.length > 1 ? "s" : ""}
              </span>
              {watchlist.filter(w => w.score_diff > 0).length > 0 && (
                <span className="px-2 py-0.5 rounded text-[9px] font-bold bg-emerald-500/15 text-emerald-400 border border-emerald-500/20">
                  {watchlist.filter(w => w.score_diff > 0).length} en hausse
                </span>
              )}
            </div>
            {showWatchlist ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </button>
          {showWatchlist && (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2">
              {watchlist.map((item) => {
                const scoreDiff = item.score_diff || 0;
                const gradeClass = GRADE_COLORS[item.current_grade] || GRADE_COLORS.C;
                const gradeChanged = item.grade_changed;
                return (
                  <div
                    key={item.ticker}
                    className="p-3 rounded-xl bg-slate-800/40 border border-slate-700/30 hover:border-yellow-500/30 transition-all cursor-pointer group relative"
                    onClick={() => onAnalyze && onAnalyze(item.ticker)}
                    data-testid={`watchlist-gem-${item.ticker}`}
                  >
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleWatchlist(item.ticker); }}
                      className="absolute top-2 right-2 p-1 rounded text-yellow-400 hover:text-red-400 hover:bg-red-500/10 transition-all opacity-0 group-hover:opacity-100"
                      title="Retirer"
                    >
                      <Star className="w-3 h-3 fill-current" />
                    </button>
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-sm font-black text-white">{item.ticker}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-black bg-gradient-to-r ${gradeClass}`}>
                        {item.current_grade}
                      </span>
                      {gradeChanged && (
                        <span className="text-[8px] text-slate-500 line-through">{item.grade_at_add}</span>
                      )}
                    </div>
                    <div className="text-[10px] text-slate-400 truncate mb-2">{item.name}</div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-cyan-400">{item.current_score}/100</span>
                        {scoreDiff !== 0 && (
                          <span className={`flex items-center gap-0.5 text-[10px] font-bold ${scoreDiff > 0 ? "text-emerald-400" : "text-red-400"}`}>
                            {scoreDiff > 0 ? <ArrowUp className="w-2.5 h-2.5" /> : <ArrowDown className="w-2.5 h-2.5" />}
                            {scoreDiff > 0 ? "+" : ""}{scoreDiff} pts
                          </span>
                        )}
                      </div>
                      {item.perf_pct !== 0 && (
                        <span className={`text-[10px] font-bold font-mono ${item.perf_pct >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                          {item.perf_pct >= 0 ? "+" : ""}{item.perf_pct}%
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* HALL OF FAME */}
      <div className="p-4 rounded-xl bg-gradient-to-r from-amber-500/5 to-yellow-500/5 border border-amber-500/20" data-testid="hall-of-fame">
        <button onClick={() => setShowHallOfFame(!showHallOfFame)} className="w-full flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-400" />
            <span className="text-sm font-black text-white">Hall of Fame — Nos Meilleures Pepites</span>
            {hallOfFame?.stats && (
              <span className="px-2 py-0.5 rounded text-[9px] font-bold bg-emerald-500/15 text-emerald-400 border border-emerald-500/20">
                {hallOfFame.stats.win_rate}% win rate | moy. {hallOfFame.stats.avg_perf > 0 ? "+" : ""}{hallOfFame.stats.avg_perf}%
              </span>
            )}
          </div>
          {showHallOfFame ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </button>
        {showHallOfFame && hallOfFame?.hall_of_fame && (
          <div className="mt-3 space-y-1.5">
            {hallOfFame.hall_of_fame.slice(0, 15).map((h, i) => (
              <div key={h.ticker} className="flex items-center justify-between p-2 rounded-lg bg-slate-800/40 hover:bg-slate-700/40 cursor-pointer transition-colors" onClick={() => onAnalyze && onAnalyze(h.ticker)} data-testid={`hof-${h.ticker}`}>
                <div className="flex items-center gap-2">
                  {i === 0 ? <Crown className="w-4 h-4 text-yellow-400" /> : i < 3 ? <Medal className="w-4 h-4 text-amber-400" /> : <span className="text-[10px] text-slate-500 w-4 text-center">{i+1}</span>}
                  <span className="text-sm font-bold text-white">{h.ticker}</span>
                  <span className="text-[10px] text-slate-500">${h.detect_price} → ${h.current_price}</span>
                </div>
                <span className={`text-sm font-black font-mono ${h.perf_pct >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {h.perf_pct >= 0 ? "+" : ""}{h.perf_pct}%
                </span>
              </div>
            ))}
            {hallOfFame.stats && (
              <div className="pt-2 mt-2 border-t border-slate-700/30 flex items-center justify-between text-[10px] text-slate-500">
                <span>{hallOfFame.stats.total_tracked} pepites trackees | {hallOfFame.stats.winners} gagnantes / {hallOfFame.stats.losers} perdantes</span>
                {hallOfFame.stats.best && (
                  <span className="text-emerald-400 font-bold">Meilleure: {hallOfFame.stats.best.ticker} +{hallOfFame.stats.best.perf_pct}%</span>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Insider Alerts Panel */}
      {insiderAlerts.length > 0 && (
        <div className="p-4 rounded-xl bg-amber-500/5 border border-amber-500/20" data-testid="insider-alerts-panel">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-amber-400 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Insider Alerts — Achats significatifs ({">"}$500K)
            </h3>
            <span className="text-[10px] text-slate-500">{insiderAlerts.length} alertes</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
            {insiderAlerts.slice(0, 6).map((alert, i) => {
              const strengthColors = {
                ULTRA: "border-yellow-500/40 bg-yellow-500/10",
                STRONG: "border-emerald-500/30 bg-emerald-500/5",
                NOTABLE: "border-slate-600/50 bg-slate-800/30",
              };
              const fmtVal = (v) => { if (v >= 1e6) return `$${(v/1e6).toFixed(1)}M`; if (v >= 1e3) return `$${(v/1e3).toFixed(0)}K`; return `$${v}`; };
              return (
                <div
                  key={i}
                  className={`p-3 rounded-lg border cursor-pointer hover:border-amber-500/40 transition-all ${strengthColors[alert.signal_strength] || strengthColors.NOTABLE}`}
                  onClick={() => onAnalyze && onAnalyze(alert.ticker)}
                  data-testid={`insider-alert-${alert.ticker}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-bold text-white">{alert.ticker}</span>
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                        alert.signal_strength === "ULTRA" ? "bg-yellow-500/20 text-yellow-400" :
                        alert.signal_strength === "STRONG" ? "bg-emerald-500/15 text-emerald-400" :
                        "bg-slate-700 text-slate-400"
                      }`}>{alert.signal_strength}</span>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded ${alert.region === "EU" ? "bg-blue-500/10 text-blue-400" : "bg-slate-700 text-slate-400"}`}>
                        {alert.region}
                      </span>
                    </div>
                    <span className="text-xs font-bold text-amber-400">{fmtVal(alert.value)}</span>
                  </div>
                  <div className="text-[10px] text-slate-400 truncate">
                    <UserCheck className="w-3 h-3 inline mr-1" />
                    {alert.insider_name} — {alert.shares?.toLocaleString()} actions @ ${alert.price}
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[9px] text-slate-500">{alert.date}</span>
                    {alert.gem_score > 0 && (
                      <span className="text-[9px] text-cyan-400">Score Pepite: {alert.gem_score}/100</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {alertsLoading && <div className="text-xs text-slate-500 mt-2 animate-pulse">Scan des insiders en cours...</div>}
        </div>
      )}

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Rechercher ticker, nom, theme..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50"
            data-testid="gems-search"
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`px-4 py-2.5 text-xs font-medium rounded-xl border transition-all flex items-center gap-2 ${
            showFilters || sectorFilter || gradeFilter || regionFilter
              ? "bg-cyan-500/10 border-cyan-500/30 text-cyan-400"
              : "bg-slate-800/50 border-slate-700/50 text-slate-400 hover:text-white"
          }`}
          data-testid="gems-filter-toggle"
        >
          <Filter className="w-3.5 h-3.5" />
          Filtres
          {(sectorFilter || gradeFilter || regionFilter) && (
            <span className="w-2 h-2 bg-cyan-400 rounded-full" />
          )}
        </button>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="p-4 rounded-xl bg-slate-800/30 border border-slate-700/30 space-y-3">
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Secteur</label>
            <div className="flex flex-wrap gap-1.5">
              <button
                onClick={() => setSectorFilter("")}
                className={`px-3 py-1 text-xs rounded-lg transition-all ${
                  !sectorFilter
                    ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30"
                    : "bg-slate-700/30 text-slate-400 hover:text-white"
                }`}
              >
                Tous
              </button>
              {sectors.map((s) => (
                <button
                  key={s}
                  onClick={() => setSectorFilter(sectorFilter === s ? "" : s)}
                  className={`px-3 py-1 text-xs rounded-lg transition-all ${
                    sectorFilter === s
                      ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30"
                      : "bg-slate-700/30 text-slate-400 hover:text-white"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Region</label>
            <div className="flex gap-1.5">
              {[["", "Tous"], ["US", "US"], ["EU", "Europe"]].map(([val, label]) => (
                <button
                  key={val}
                  onClick={() => setRegionFilter(regionFilter === val ? "" : val)}
                  className={`px-3 py-1 text-xs rounded-lg transition-all font-bold ${
                    regionFilter === val
                      ? val === "EU" ? "bg-blue-500/20 text-blue-400 border border-blue-500/30"
                        : val === "US" ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30"
                        : "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30"
                      : "bg-slate-700/30 text-slate-400 hover:text-white"
                  }`}
                >
                  {val === "EU" && <Globe className="w-3 h-3 inline mr-1" />}
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Grade</label>
            <div className="flex gap-1.5">
              {["", "S", "A", "B", "C"].map((g) => (
                <button
                  key={g}
                  onClick={() => setGradeFilter(gradeFilter === g ? "" : g)}
                  className={`px-3 py-1 text-xs rounded-lg transition-all font-bold ${
                    gradeFilter === g
                      ? `bg-gradient-to-r ${GRADE_COLORS[g] || "from-cyan-500 to-blue-500 text-white"}`
                      : "bg-slate-700/30 text-slate-400 hover:text-white"
                  }`}
                >
                  {g || "Tous"}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <Gem className="w-10 h-10 text-yellow-400 animate-pulse mb-4" />
          <p className="text-sm text-slate-400">
            Analyse de {gems.length || "150+"} pepites en cours...
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Fondamentaux + Insiders + Institutions + IA
          </p>
        </div>
      ) : (
        <>
          {/* Results count */}
          <div className="text-xs text-slate-500">
            {filtered.length} pepite{filtered.length !== 1 ? "s" : ""} affichee{filtered.length !== 1 ? "s" : ""}
            {(searchQuery || sectorFilter || gradeFilter) && ` (filtrees)`}
          </div>

          {/* Gems Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {filtered.map((gem, idx) => (
              <GemCard
                key={gem.ticker}
                gem={gem}
                rank={idx + 1}
                onSelect={(ticker) => onAnalyze && onAnalyze(ticker)}
                onThesis={handleThesis}
                thesisLoading={thesisLoading === gem.ticker}
                isInWatchlist={watchlistTickers.has(gem.ticker)}
                onToggleWatchlist={toggleWatchlist}
              />
            ))}
          </div>

          {filtered.length === 0 && !loading && (
            <div className="text-center py-16 text-slate-500">
              <Gem className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p>Aucune pepite trouvee avec ces filtres</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
